import { MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { Menu, type MenuItem } from '../../../../components/Menu';

/**
 * A tier column's own menu, opened from its header.
 *
 * The chart is where a reader sees the tiers as a ladder, so it is where editing one belongs
 * — the alternative is leaving for Organization settings and coming back to see whether the
 * column moved.
 */
export function LaneActions({
  tierId,
  label,
  isLast,
  unitCount,
  onEdit,
  onDelete,
}: {
  tierId: string;
  label: string;
  /** The last column. Only its tier can be removed without stranding the ones after it. */
  isLast: boolean;
  /** How many units carry this tier. Stated in the confirmation rather than guessed at. */
  unitCount: number;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const items: MenuItem[] = [
    {
      key: 'edit-tier',
      label: 'Edit',
      icon: <Pencil className="h-4 w-4" aria-hidden="true" />,
      permission: 'org-unit-types.update',
      onClick: onEdit,
    },
    {
      key: 'delete-tier',
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4" aria-hidden="true" />,
      permission: 'org-unit-types.delete',
      destructive: true,
      divider: true,
      /*
        Disabled for STATE, so it says why rather than sitting there dead.

        Only the last tier can go from here. Removing one in the middle would leave every
        column after it a rung out of step with the ladder the ranks describe, and the units
        in them ranked against a tier that no longer exists — a re-rank of the whole chart
        dressed up as a delete.

        Every tier has a column now, occupied or not, so this reaches all of them — but
        Organization settings still lists them with their descriptions and their deleted ones,
        which is more than a column head can hold.
      */
      disabledReason: isLast
        ? null
        : 'Only the last tier can be deleted here. Use Organization settings for any other tier, or move the units in the tiers after it first.',
      onClick: onDelete,
    },
  ];

  return (
    <Menu
      id={`tier-${tierId}-menu`}
      items={items}
      align="left"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          id={`tier-${tierId}-actions`}
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          // Named, because a row of identical kebabs is otherwise unreadable to a screen
          // reader — "Tier actions" three times says nothing about which column.
          aria-label={`Actions for the ${label} tier${unitCount > 0 ? `, ${unitCount} units` : ''}`}
          className="inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
        >
          <MoreVertical className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    />
  );
}
